import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { t } from "i18n";

// Shared impact visuals: the severity scale, meter, rise/decay pulse, axis
// bars, and detail-row builder used by both the serial terminal's dark event
// tooltip and the Live tab's light expanded rows. Every component takes a
// `dark` flag (default true, the terminal's home theme) so the terminal
// renders byte-for-byte as before with no call-site changes.

export const gStr = (mg) =>
  typeof mg === "number" ? `${(mg / 1000).toFixed(2)} g` : "—";

// Severity by peak |a|. Colours graduate green→amber→orange→red so a hard hit
// reads instantly, independent of the firmware's own detection `band`.
export const IMPACT_FS_G = 60; // meter full-scale
export const IMPACT_BANDS = [10, 25, 50]; // light | moderate | hard | severe

const SEV = {
  dark: ["#4ADE80", "#FBBF24", "#FB923C", "#F87171"],
  light: ["#16A34A", "#D97706", "#EA580C", "#DC2626"],
};

// `level` is the stable severity token (matches the firmware's `band` names);
// `label` is its translated display text. Compare against `level`, not `label`.
const sevResult = (color, level) => ({
  color,
  level,
  label: t(`analysis.impactVisuals.severity.${level}`),
});

export const severityForG = (g, dark = true) => {
  const c = dark ? SEV.dark : SEV.light;
  if (typeof g !== "number")
    return { color: dark ? "#6B7280" : "#9CA3AF", level: null, label: "—" };
  if (g < IMPACT_BANDS[0]) return sevResult(c[0], "light");
  if (g < IMPACT_BANDS[1]) return sevResult(c[1], "moderate");
  if (g < IMPACT_BANDS[2]) return sevResult(c[2], "hard");
  return sevResult(c[3], "severe");
};

// Horizontal severity meter: a filled bar on a fixed 0–60 g scale, coloured by
// severity, with faint band ticks and a peak cap.
export const ImpactMeter = ({ peakMg, w = 124, h = 20, dark = true }) => {
  if (typeof peakMg !== "number") return null;
  const g = peakMg / 1000;
  const { color } = severityForG(g, dark);
  const pad = 2;
  const trackW = w - 2 * pad;
  const midY = h / 2;
  const frac = Math.min(g / IMPACT_FS_G, 1);
  const fillW = Math.max(3, frac * trackW);
  const over = g >= IMPACT_FS_G;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="flex-shrink-0 self-center"
    >
      <rect
        x={pad}
        y={midY - 4}
        width={trackW}
        height={8}
        rx={4}
        fill={dark ? "#FFFFFF" : "#111827"}
        fillOpacity={dark ? "0.05" : "0.06"}
      />
      {IMPACT_BANDS.map((tg) => {
        const tx = pad + (tg / IMPACT_FS_G) * trackW;
        return (
          <line
            key={tg}
            x1={tx}
            y1={midY - 5}
            x2={tx}
            y2={midY + 5}
            stroke={dark ? "#4E5666" : "#D1D5DB"}
            strokeOpacity={dark ? "0.5" : "0.9"}
            strokeWidth="1"
          />
        );
      })}
      <rect x={pad} y={midY - 4} width={fillW} height={8} rx={4} fill={color} />
      <circle
        cx={pad + fillW}
        cy={midY}
        r="3"
        fill={color}
        stroke={dark ? "#0C0E14" : "#FFFFFF"}
        strokeWidth="1"
      />
      {over && (
        <text
          x={w - 2}
          y={midY + 3}
          fontSize="9"
          fontFamily="monospace"
          fill={dark ? "#F87171" : "#DC2626"}
          textAnchor="end"
        >
          +
        </text>
      )}
    </svg>
  );
};

// Attack/decay pulse: rises over `rise_ms` to the peak then falls to `dur_ms`,
// so the shape of the hit is visible at a glance.
export const ImpactPulse = ({
  riseMs,
  durMs,
  color = "#F472B6",
  w = 302,
  h = 44,
  dark = true,
}) => {
  const { t } = useTranslation();
  const total = Math.max(durMs || 0, (riseMs || 0) + 1, 1);
  const pad = 3;
  const innerW = w - 2 * pad;
  const baseY = h - pad - 8;
  const peakY = pad + 1;
  const peakX =
    pad + (Math.min(riseMs ?? total * 0.15, total) / total) * innerW;
  const endX = pad + (Math.min(durMs ?? total, total) / total) * innerW;
  const shape = `${pad},${baseY} ${peakX.toFixed(1)},${peakY} ${endX.toFixed(
    1
  )},${baseY}`;
  const label = dark ? "#7A8394" : "#9CA3AF";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="w-full">
      <line
        x1={pad}
        y1={baseY}
        x2={w - pad}
        y2={baseY}
        stroke={dark ? "#242A38" : "#E7E5E1"}
        strokeWidth="1"
      />
      <polygon points={shape} fill={color} fillOpacity="0.12" />
      <polyline points={shape} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={peakX} cy={peakY} r="2.6" fill={color} />
      {riseMs != null && (
        <text
          x={peakX}
          y={h - 1}
          fontSize="8.5"
          fontFamily="monospace"
          fill={label}
          textAnchor="middle"
        >
          {t("analysis.impactVisuals.pulseRise", { ms: riseMs })}
        </text>
      )}
      {durMs != null && (
        <text
          x={w - pad}
          y={h - 1}
          fontSize="8.5"
          fontFamily="monospace"
          fill={label}
          textAnchor="end"
        >
          {t("analysis.impactVisuals.pulseDuration", { ms: durMs })}
        </text>
      )}
    </svg>
  );
};

// Signed X/Y/Z bars diverging from a centre axis — shows an impact's direction
// and per-axis magnitude at a glance. Axis colours follow the 3-D convention
// (X red, Y green, Z blue). `labels` adds the axis letters + g values.
const AXES = {
  dark: [
    { key: "x", color: "#F87171" },
    { key: "y", color: "#4ADE80" },
    { key: "z", color: "#60A5FA" },
  ],
  light: [
    { key: "x", color: "#DC2626" },
    { key: "y", color: "#16A34A" },
    { key: "z", color: "#2563EB" },
  ],
};

export const AxisBars = ({
  comps,
  w = 124,
  h = 20,
  labels = false,
  dark = true,
}) => {
  if (!comps || comps.every((c) => typeof c !== "number")) return null;
  const axes = dark ? AXES.dark : AXES.light;
  const max = Math.max(1000, ...comps.map((c) => Math.abs(c || 0)));
  const padL = labels ? 16 : 3;
  const padR = labels ? 42 : 3;
  const cx = padL + (w - padL - padR) / 2;
  const half = (w - padL - padR) / 2;
  const rowH = h / 3;
  const barH = Math.max(3, rowH - (labels ? 4 : 3));
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="flex-shrink-0 self-center"
    >
      <line
        x1={cx}
        y1={1}
        x2={cx}
        y2={h - 1}
        stroke={dark ? "#4E5666" : "#D1D5DB"}
        strokeOpacity={dark ? "0.5" : "0.9"}
        strokeWidth="1"
      />
      {axes.map((a, i) => {
        const v = comps[i] || 0;
        const len = Math.max(1, (Math.abs(v) / max) * half);
        const y = i * rowH + (rowH - barH) / 2;
        const x = v >= 0 ? cx : cx - len;
        return (
          <g key={a.key}>
            {labels && (
              <text
                x={2}
                y={y + barH}
                fontSize="8"
                fontFamily="monospace"
                fill={a.color}
              >
                {a.key.toUpperCase()}
              </text>
            )}
            <rect
              x={x}
              y={y}
              width={len}
              height={barH}
              rx={1.5}
              fill={a.color}
              fillOpacity="0.9"
            />
            {labels && (
              <text
                x={w - 2}
                y={y + barH}
                fontSize="8"
                fontFamily="monospace"
                fill={dark ? "#9CA3AF" : "#6B7280"}
                textAnchor="end"
              >
                {gStr(v)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// Inline sparkline for a 0x11 load-snippet tx. Each sample is a peak-hold |a|
// magnitude (gravity included, so rest ≈ 1 g) on a fixed 0–10 g scale — the
// wire's full scale — so consecutive snippets compare directly. We mark the
// 1 g gravity baseline, the 2 g trigger, and, when any slice pegs at 10 g,
// an off-scale line (≥10 g clamps flat; the true peak is in the flash record).
export const SNIPPET_FS_MG = 10000;
export const SnippetSparkline = ({
  samples,
  w = 132,
  h = 20,
  interactive = false,
  dark = true,
}) => {
  const [hi, setHi] = useState(null); // hovered sample index
  if (!Array.isArray(samples) || samples.length < 2) return null;
  const line = dark ? "#2DD4BF" : "#0D9488";
  const n = samples.length;
  const pad = 1.5;
  const x = (i) => (i / (n - 1)) * (w - 2 * pad) + pad;
  const y = (mg) =>
    h -
    pad -
    (Math.min(Math.max(mg, 0), SNIPPET_FS_MG) / SNIPPET_FS_MG) * (h - 2 * pad);
  const pts = samples
    .map((mg, i) => `${x(i).toFixed(1)},${y(mg).toFixed(1)}`)
    .join(" ");
  const saturated = samples.some((s) => s >= SNIPPET_FS_MG);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - r.left - pad) / (w - 2 * pad);
    setHi(Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1)))));
  };

  let cursor = null;
  if (interactive && hi != null) {
    const cx = x(hi);
    const cy = y(samples[hi]);
    const label = `${(samples[hi] / 1000).toFixed(2)} g · ${hi * 50} ms`;
    const tw = label.length * 5.1 + 8;
    const lx = Math.max(1, Math.min(w - tw - 1, cx - tw / 2));
    cursor = (
      <g pointerEvents="none">
        <line
          x1={cx}
          y1={pad}
          x2={cx}
          y2={h - pad}
          stroke={dark ? "#E5E7EB" : "#111827"}
          strokeOpacity="0.3"
          strokeWidth="1"
        />
        <circle
          cx={cx}
          cy={cy}
          r="2.6"
          fill={line}
          stroke={dark ? "#0C0E14" : "#FFFFFF"}
          strokeWidth="1"
        />
        <rect x={lx} y={2} width={tw} height={13} rx="3" fill="#1D2130" />
        <text
          x={lx + 4}
          y={11.5}
          fontSize="9"
          fontFamily="monospace"
          fill="#E5E7EB"
        >
          {label}
        </text>
      </g>
    );
  }

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      onMouseMove={interactive ? onMove : undefined}
      onMouseLeave={interactive ? () => setHi(null) : undefined}
      className={`flex-shrink-0 self-center rounded ${
        dark ? "bg-white/[0.03]" : "bg-[#111827]/[0.03]"
      } ${interactive ? "cursor-crosshair" : ""}`}
    >
      {/* 1 g gravity baseline */}
      <line
        x1="0"
        y1={y(1000)}
        x2={w}
        y2={y(1000)}
        stroke="#9CA3AF"
        strokeOpacity={dark ? "0.22" : "0.45"}
        strokeWidth="1"
      />
      {/* 2 g trigger threshold */}
      <line
        x1="0"
        y1={y(2000)}
        x2={w}
        y2={y(2000)}
        stroke="#E07A3A"
        strokeOpacity="0.5"
        strokeDasharray="3,2"
        strokeWidth="1"
      />
      {/* off-scale (≥10 g) */}
      {saturated && (
        <line
          x1="0"
          y1={y(SNIPPET_FS_MG)}
          x2={w}
          y2={y(SNIPPET_FS_MG)}
          stroke={dark ? "#F87171" : "#DC2626"}
          strokeOpacity="0.55"
          strokeWidth="1"
        />
      )}
      <polyline points={pts} fill="none" stroke={line} strokeWidth="1.3" />
      {cursor}
    </svg>
  );
};

// ts_ms is epoch-ms when the clock was synced, else device uptime-ms. Values
// past ~year 2001 (1e12 ms) can only be epoch; render those as a local
// date/time, and show uptime otherwise.
export const fmtEventTs = (ms) => {
  if (typeof ms !== "number") return String(ms);
  if (ms >= 1e12)
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  const s = ms / 1000;
  return t("analysis.impactVisuals.uptime", {
    value:
      s >= 60
        ? `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`
        : `${s.toFixed(1)}s`,
  });
};

// Getters so the title is translated at read time, not frozen at module load.
export const TX_TITLE = {
  get "0x10"() {
    return t("analysis.impactVisuals.txTitle.impactBroadcast");
  },
  get "0x11"() {
    return t("analysis.impactVisuals.txTitle.loadSnippet");
  },
  get "0x12"() {
    return t("analysis.impactVisuals.txTitle.statusBroadcast");
  },
};

// Peak angular velocity, milli-dps -> a readable string. rad/s alongside dps
// because the head-impact literature is written in rad/s and dps is what the
// sensor reports; showing one without the other means constant conversion.
export const dpsStr = (mdps) => {
  if (typeof mdps !== "number") return "-";
  const dps = mdps / 1000;
  const rads = dps * (Math.PI / 180);
  return `${dps.toFixed(dps < 100 ? 1 : 0)} dps · ${rads.toFixed(1)} rad/s`;
};

// Dry-run gate verdict token -> plain words. The firmware evaluates these and
// RECORDS them; it never rejects, so none of these mean the event was dropped.
// Token -> translation key suffix (analysis.impactVisuals.gateVerdict.*).
export const GATE_VERDICT = {
  PASS: "pass",
  NARROW: "tooNarrow",
  WIDE: "tooWide",
  NOROT: "noRotation",
};

export const gateVerdictLabel = (gate) => {
  if (!gate) return null;
  if (gate === "n/a")
    return t("analysis.impactVisuals.gateVerdict.notEvaluated");
  if (gate === "?") return t("analysis.impactVisuals.gateVerdict.unknown");
  return gate
    .split("|")
    .map((g) =>
      GATE_VERDICT[g]
        ? t(`analysis.impactVisuals.gateVerdict.${GATE_VERDICT[g]}`)
        : g.toLowerCase()
    )
    .join(" + ");
};

// The ordered label/value rows shown in a detail card for one event.
export const eventDetailRows = (o) => {
  const kind = o.t;
  const pkt = o.pkt;
  const L = (key) => t(`analysis.impactVisuals.detail.${key}`);
  if (kind === "impact")
    return [
      [L("impactId"), o.id],
      o.peak_mg != null && [L("peak"), `${gStr(o.peak_mg)} · ${o.peak_mg} mg`],
      o.band && [L("band"), o.band],
      o.rise_ms != null && [L("rise"), `${o.rise_ms} ms`],
      o.dur_ms != null && [L("duration"), `${o.dur_ms} ms`],
      o.over != null && [L("samplesOver"), o.over],
      // Dry-run gate verdict (fw v0.6.2+). Spelled out rather than shown as the
      // raw token, because "NARROW" alone reads like an error and this is a
      // diagnostic: the event was kept either way.
      o.gate && [L("gateDryRun"), gateVerdictLabel(o.gate)],
      // Peak rotation during the event (fw v0.6.4+). gyro_ok false means no
      // gyro sample reached the event, so the rotation test was SKIPPED --
      // never conflate that with a measured zero.
      o.gyro_mdps != null &&
        o.gyro_ok !== false && [L("peakRotation"), dpsStr(o.gyro_mdps)],
      o.gyro_ok === false && [L("peakRotation"), L("noGyroSample")],
    ].filter(Boolean);
  if (kind === "tx" && pkt === "0x11")
    return [
      [L("eventId"), o.id],
      [
        L("window"),
        t("analysis.impactVisuals.detail.windowValue", {
          ms: o.window_ms ?? 50,
        }),
      ],
      o.min_mg != null && [L("min"), `${gStr(o.min_mg)} · ${o.min_mg} mg`],
      o.max_mg != null && [L("peak"), `${gStr(o.max_mg)} · ${o.max_mg} mg`],
      Array.isArray(o.samples_mg) && [L("samples"), o.samples_mg.length],
    ].filter(Boolean);
  if (kind === "tx" && pkt === "0x10")
    return [
      [L("impactId"), o.id],
      o.tag && [L("tag"), o.tag],
      [L("magnitude"), gStr(o.mag_mg)],
      ["X / Y / Z", `${gStr(o.x_mg)} / ${gStr(o.y_mg)} / ${gStr(o.z_mg)}`],
      o.ts_ms != null && [L("timestamp"), fmtEventTs(o.ts_ms)],
    ].filter(Boolean);
  if (kind === "tx" && pkt === "0x12") {
    const fl = [];
    if (o.flags & 1) fl.push("charging");
    if (o.flags & 2) fl.push("battery_low");
    if (o.flags & 4) fl.push("imu_fault");
    return [
      o.impacts != null && [L("impacts"), o.impacts],
      o.bcasts != null && [L("broadcasts"), o.bcasts],
      o.offline != null && [L("offline"), o.offline],
      o.batt_pct != null && [
        L("battery"),
        `${o.batt_pct}%${o.batt_mv ? ` · ${o.batt_mv} mV` : ""}${
          o.flags & 1 ? ` · ${L("chargingInflated")}` : ""
        }`,
      ],
      [L("flags"), fl.length ? fl.join(", ") : L("none")],
      o.ts_ms != null && [L("timestamp"), fmtEventTs(o.ts_ms)],
    ].filter(Boolean);
  }
  // Generic: every scalar field, mg → g where it applies.
  return Object.entries(o)
    .filter(
      ([k, v]) => k !== "t" && k !== "raw" && v != null && typeof v !== "object"
    )
    .map(([k, v]) => [
      k,
      k.endsWith("_mg") && typeof v === "number" ? gStr(v) : String(v),
    ]);
};
